import {
  ContractStatuses,
  SettlementDateStatuses,
  VendorBankDetailsStatuses,
} from '@urbanx/agx.campaigns.client';
import { States } from '@urbanx/agx.campaigns.client';
import { normalizeAgencyFilterState } from 'utils/agencyUtils';

export interface ContractsDashboardFilterState {
  states?: States[];
  agencyIds?: string[];
  leadAgentIds?: string[];
  contractStatuses?: ContractStatuses[];
  settlementDateStatuses?: SettlementDateStatuses[];
  vendorBankDetailsStatuses?: VendorBankDetailsStatuses[];
}

type StoredContractsDashboardFilterState = Partial<ContractsDashboardFilterState> & {
  agencyValues?: string[];
};

export const contractsDashboardSettlementDateStatuses = [
  SettlementDateStatuses.ThisWeek,
  SettlementDateStatuses.NextWeek,
] as const;

export const defaultContractsDashboardFilterState: ContractsDashboardFilterState = {
  states: undefined,
  agencyIds: undefined,
  leadAgentIds: undefined,
  contractStatuses: undefined,
  settlementDateStatuses: undefined,
  vendorBankDetailsStatuses: undefined,
};

const contractsDashboardFiltersKey = 'contractsDashboardFilters';

const isValidEnumArray = <T extends string>(
  values: unknown,
  allowedValues: readonly T[]
): values is T[] =>
  Array.isArray(values) &&
  values.every((value) => typeof value === 'string' && allowedValues.includes(value as T));

export const storeContractsDashboardFilterState = (filters: ContractsDashboardFilterState) => {
  try {
    const { agencyIds, ...storedFilters } = filters;
    localStorage.setItem(
      contractsDashboardFiltersKey,
      JSON.stringify({ ...storedFilters, agencyValues: agencyIds })
    );
  } catch {
    // Ignore storage failures so the dashboard still works normally.
  }
};

export const createInitialContractsDashboardFilterState = (): ContractsDashboardFilterState => {
  try {
    const storedFilterState = localStorage.getItem(contractsDashboardFiltersKey);

    if (!storedFilterState) return defaultContractsDashboardFilterState;

    const parsed = JSON.parse(storedFilterState) as StoredContractsDashboardFilterState | null;

    if (!parsed) return defaultContractsDashboardFilterState;

    return {
      states: isValidEnumArray(parsed.states, [States.Qld, States.Nsw])
        ? parsed.states
        : defaultContractsDashboardFilterState.states,
      ...normalizeAgencyFilterState(parsed),
      contractStatuses: isValidEnumArray(parsed.contractStatuses, Object.values(ContractStatuses))
        ? parsed.contractStatuses
        : defaultContractsDashboardFilterState.contractStatuses,
      settlementDateStatuses: isValidEnumArray(
        parsed.settlementDateStatuses,
        contractsDashboardSettlementDateStatuses
      )
        ? parsed.settlementDateStatuses
        : defaultContractsDashboardFilterState.settlementDateStatuses,
      vendorBankDetailsStatuses: isValidEnumArray(
        parsed.vendorBankDetailsStatuses,
        Object.values(VendorBankDetailsStatuses)
      )
        ? parsed.vendorBankDetailsStatuses
        : defaultContractsDashboardFilterState.vendorBankDetailsStatuses,
    };
  } catch {
    return defaultContractsDashboardFilterState;
  }
};
